import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supplierRefundApi } from '@/api/supplier'
import type { RefundListParams, RefundProcessParams } from '@/schemas/supplier'

// Query Keys
export const supplierRefundKeys = {
  all: ['supplier', 'refunds'] as const,
  lists: () => [...supplierRefundKeys.all, 'list'] as const,
  list: (params: RefundListParams) => [...supplierRefundKeys.lists(), params] as const,
  details: () => [...supplierRefundKeys.all, 'detail'] as const,
  detail: (id: number) => [...supplierRefundKeys.details(), id] as const,
  reasons: () => [...supplierRefundKeys.all, 'reasons'] as const,
}

/**
 * 获取退款列表
 */
export function useSupplierRefunds(params: RefundListParams) {
  return useQuery({
    queryKey: supplierRefundKeys.list(params),
    queryFn: () => supplierRefundApi.list(params),
  })
}

/**
 * 获取退款详情
 */
export function useSupplierRefund(id: number, enabled = true) {
  return useQuery({
    queryKey: supplierRefundKeys.detail(id),
    queryFn: () => supplierRefundApi.detail(id),
    enabled: enabled && id > 0,
  })
}

/**
 * 获取退款原因列表
 */
export function useRefundReasons() {
  return useQuery({
    queryKey: supplierRefundKeys.reasons(),
    queryFn: () => supplierRefundApi.reasons(),
    staleTime: 5 * 60 * 1000, // 5分钟缓存
  })
}

/**
 * 处理退款
 */
export function useRefundProcess() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: RefundProcessParams }) =>
      supplierRefundApi.process(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierRefundKeys.lists() })
      queryClient.invalidateQueries({ queryKey: supplierRefundKeys.details() })
      // 退款变化可能影响订单状态
      queryClient.invalidateQueries({ queryKey: ['supplier', 'orders'] })
      toast.success('退款处理成功')
    },
  })
}

/**
 * 确认收货并退款
 */
export function useRefundConfirmReceive() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: number) => supplierRefundApi.confirmReceive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierRefundKeys.lists() })
      queryClient.invalidateQueries({ queryKey: supplierRefundKeys.details() })
      queryClient.invalidateQueries({ queryKey: ['supplier', 'orders'] })
      toast.success('确认收货并退款成功')
    },
  })
}

/**
 * 刷新退款列表
 */
export function useSupplierRefundListRefresh() {
  const queryClient = useQueryClient()

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: supplierRefundKeys.lists() })
  }

  return { refresh }
}
